import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import MediaFilters from './media-filters';
import { MediaList } from './media-display';
import Layout from './Layout';
import MetaTags from './meta-tags';
import apiService from '../services/api-service';
import { trackSearch } from '../utils/gtm';
import { SPINNER_CLASSES, BUTTON_CLASSES } from '../styles';
import useUnifiedFilters from '../hooks/use-unified-filters';
import useViewMode from '../hooks/use-view-mode';
import useReloadReset from '../hooks/use-reload-reset';
import { MOVIE_GENRES, MOVIE_SORT_OPTIONS } from '../constants';
import { applySearchResultFilters } from '../utils/search-result-filters';

function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  const [viewMode, setViewMode] = useViewMode();
  const {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters
  } = useUnifiedFilters({}, 'movie');

  useReloadReset(resetFilters, setViewMode);

  const stableFilters = React.useMemo(() => ({
    selectedGenre: filters.selectedGenre || 'all',
    sortBy: filters.sortBy || 'trending',
    fromYear: filters.fromYear || '',
    toYear: filters.toYear || '',
    imdbRating: filters.imdbRating || 0,
    rottenTomatoesRating: filters.rottenTomatoesRating || 0,
    availableLT: filters.availableLT || 'false'
  }), [
    filters.selectedGenre,
    filters.sortBy,
    filters.fromYear,
    filters.toYear,
    filters.imdbRating,
    filters.rottenTomatoesRating,
    filters.availableLT
  ]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    isError,
    refetch
  } = useInfiniteQuery({
    queryKey: ['search', 'multi', query],
    queryFn: async ({ pageParam = 1 }) => {
      if (!query.trim()) {
        return { results: [], total_results: 0, total_pages: 0 };
      }
      return await apiService.searchMulti(query, pageParam);
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalPages = lastPage.total_pages || 0;

      if (currentPage >= totalPages || !lastPage.results?.length) {
        return undefined;
      }

      return currentPage + 1;
    },
    initialPageParam: 1,
    enabled: !!query.trim(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const allResults = React.useMemo(() => {
    if (!data?.pages) return [];
    return data.pages
      .flatMap(page => page.results || [])
      .filter(item => {
        if (!item.poster_path) return false;
        if (!item.overview || item.overview.trim() === '') return false;
        return true;
      });
  }, [data?.pages]);

  const filteredResults = React.useMemo(
    () => applySearchResultFilters(allResults, stableFilters),
    [allResults, stableFilters]
  );

  const apiTotalResults = React.useMemo(() => {
    if (!data?.pages?.length) return 0;
    for (const page of data.pages) {
      if (page.total_results > 0) return page.total_results;
    }
    return data.pages[0]?.total_results || 0;
  }, [data?.pages]);

  React.useEffect(() => {
    if (query.trim() && allResults.length > 0 && !isLoading) {
      trackSearch(query, allResults.length);
    }
  }, [query, allResults.length, isLoading]);

  React.useEffect(() => {
    if (!query.trim()) {
      navigate('/');
    }
  }, [query, navigate]);

  const metaTitle = `Paieška: "${query}" - FilmųDB`;
  const metaUrl = `https://filmudb.lt/search?q=${encodeURIComponent(query)}`;

  const filterBlock = query.trim() ? (
    <MediaFilters
      mediaType="movie"
      hideTitle
      hideAvailableLT
      subtitle={`Paieškos rezultatai: "${query}"`}
      filters={filters}
      onFiltersChange={updateFilters}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      genres={MOVIE_GENRES}
      sortOptions={MOVIE_SORT_OPTIONS}
      resetFilters={resetFilters}
      hasActiveFilters={hasActiveFilters}
      resetViewMode={setViewMode}
    />
  ) : null;

  if (isError) {
    return (
      <Layout resetFilters={resetFilters}>
        <MetaTags
          title={metaTitle}
          description={`Paieškos rezultatai pagal "${query}"`}
          url={metaUrl}
          keywords={`${query}, filmų paieška, TV serialų paieška, filmai, serialai`}
        />
        <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
          {filterBlock}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center py-12">
              <div className="text-red-400 mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-white mb-2">Klaida atliekant paiešką</h3>
                <p className="text-gray-400 mb-4">{error?.message || 'Įvyko nežinoma klaida'}</p>
                <button type="button" onClick={() => refetch()} className={BUTTON_CLASSES.PRIMARY_SM}>
                  Bandyti dar kartą
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout resetFilters={resetFilters}>
        <MetaTags
          title={metaTitle}
          description={`Paieškos rezultatai pagal "${query}"`}
          url={metaUrl}
          keywords={`${query}, filmų paieška, TV serialų paieška, filmai, serialai`}
        />
        <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
          {filterBlock}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-center justify-center py-12">
              <div className={SPINNER_CLASSES.XLARGE_PRIMARY}></div>
              <span className="ml-3 text-gray-400">Ieškoma...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!allResults.length && query.trim()) {
    return (
      <Layout resetFilters={resetFilters}>
        <MetaTags
          title={metaTitle}
          description={`Paieškos rezultatai pagal "${query}"`}
          url={metaUrl}
          keywords={`${query}, filmų paieška, TV serialų paieška, filmai, serialai`}
        />
        <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
          {filterBlock}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-white mb-2">Rezultatų nerasta</h3>
                <p className="text-gray-400">
                  Pagal &quot;{query}&quot; nieko nerasta. Pabandykite keisti paieškos žodžius.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout resetFilters={resetFilters}>
      <MetaTags
        title={metaTitle}
        description={allResults.length > 0
          ? `Rasta ${allResults.length.toLocaleString()} rezultatų pagal "${query}". Ieškokite filmų ir TV serialų FilmųDB platformoje.`
          : `Paieškos rezultatai pagal "${query}"`
        }
        url={metaUrl}
        keywords={`${query}, filmų paieška, TV serialų paieška, filmai, serialai`}
      />
      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        <section className="relative pt-6 pb-4 sm:pt-8 sm:pb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">Paieškos rezultatai</h1>
                <p className="text-gray-400 mt-1">
                  {filteredResults.length.toLocaleString()} rezultatai pagal &quot;{query}&quot;
                  {hasActiveFilters && apiTotalResults > 0 && (
                    <span className="text-gray-500"> (iš užkrautų {allResults.length.toLocaleString()})</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {filterBlock}

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MediaList
            mediaType="movie"
            scrollRestorationMediaType="search"
            useItemMediaType
            searchQuery={query}
            filters={stableFilters}
            viewMode={viewMode}
            items={filteredResults}
            totalCount={hasActiveFilters ? filteredResults.length : apiTotalResults}
            isLoading={false}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            error={null}
            isError={false}
            isRateLimited={false}
            refetch={refetch}
          />
        </main>
      </div>
    </Layout>
  );
}

export default SearchResults;
